/**
 * Part del State necessari per el mapa que mostra la posició del procediment
 * Component: MapaState.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 29/09/2017
 * (c) Ajuntament de Manresa - set 2017 - Departament de Sistemes d'Informació
 */
import { observable, action } from 'mobx';
import AjtAjaxAxios from 'ajt-ajax';
import ol, { MapBrowserEvent } from 'openlayers';
import OlLayerBase from 'ol/layer/Base';
import OlCollection from 'ol/Collection';
import OlLayerImage from 'ol/layer/Image';
import OlLayerTile from 'ol/layer/Tile';
import OlLayerVector from 'ol/layer/Vector';
import OlProjection from 'ol/proj/Projection';
import OlSourceImageWMS from 'ol/source/ImageWMS';
import OlSourceTileWMS from 'ol/source/TileWMS';
import OlSourceWMTS from 'ol/source/WMTS';
import OlSourceVector from 'ol/source/Vector';
import OlView from 'ol/View';
import OlMap from 'ol/Map';
import { Extent, getCenter } from 'ol/extent';
import { Coordinate } from 'ol/coordinate';
import { IPropsMapa } from '../interfaces/IPropsMapa';
import { IMapConfig } from '../interfaces/IMapConfig';
import AdrWidgetAPI from '../api/AdrWidgetAPI';
import Util from '../config/util';
import { ICatLot } from '../interfaces/ICatLot';
import { ILot } from '../interfaces/ILot';
import OlGeoJSON from 'ol/format/GeoJSON';
import Geometry from 'ol/geom/Geometry';
import Feature, { FeatureLike } from 'ol/Feature';
import OlStyleFill from 'ol/style/Fill';
import OlStyleStroke from 'ol/style/Stroke';
import OlStyleStyle, { StyleFunction } from 'ol/style/Style';

export default class MapaState {
    // Constructor
    constructor(props: IPropsMapa) {
        this._props = props;
        this.mapConfig = this._props.appState.mapConfig;
        this.olproj = this._props.appState.olproj;
        this.adrWebApi = new AdrWidgetAPI(this._props.appState.mapConfig.adrWebApiUrl);
        this.createMap();
        this.catsLots = this.createCats();
    }

    // Estensió inicial del mapa
    public readonly initialExtent: [number, number, number, number] = [397000, 4615700, 408700, 4623700];

    // El marge per trobar un número al fer "click" al mapa (en metres, s'utilitza a la funció setFIDFromEvt)
    protected static readonly margin: number = 2.0; 

    // Una còpia de les propietats
    private _props: IPropsMapa;

    // Per accedir més fàcilment a les MapConfig
    public mapConfig: IMapConfig;

    // La capa de lots
    public lotsLayer: OlLayerTile | OlLayerImage;

    // Request
    public req: AjtAjaxAxios = new AjtAjaxAxios();

    // La nostra projecció
    public olproj: OlProjection;

    // L'interface cap a l'API per cercar adreces i referències cadastrals
    public adrWebApi: AdrWidgetAPI = null;

    // L'amplada de la pàgina per calcular les del mapa
    @observable width: number = 800;

    // L'alçada' de la pàgina per calcular les del mapa
    @observable height: number = 600;

    // L'ultim numcod triat
    @observable numCod: number = 0;

    // Dades de l'element identificat
    @observable elementData?: ILot = null;

    // Per controlar l'estat del HorizonCollapse
    @observable hcExpanded: boolean = false;

    // Per controlar l'estat del HorizonCollapse del RefLayers
    @observable rlHCExpanded: boolean = false;

    // Array amb les categories i número de lots i fanals per catgoria
    @observable catsLots: Array<ICatLot> = [];

    // Variable auxiliar per repintar els checkBoxes quan es seleccionen o deseleccionen
    @observable rlAux: number = 0;

    // La llista de capes base
    public baseLayers: Array<OlLayerTile | OlLayerImage> = []; // @observable

    // La llista de capes de referència
    public refLayers: Array<OlLayerTile | OlLayerImage> = []; // @observable

    // La vista del mapa
    public olview?: OlView; // @observable

    // El mapa
    public olmap?: OlMap; // @observable

    // PEr guardar les geometries com a GeoJSON
    private gjson = new OlGeoJSON();

    // La capa "markers" per el lot a resaltar
    private lotOrdLayer?: OlLayerVector;

    // El source "markers" per el lot a resaltar
    public lotOrdSource?: OlSourceVector;

    // Construim el mapa i hi afegim la llista de capes i el controls estàndard
    private createMap() { // @observable
        this._props.appState.mapConfig.baseLayers.forEach((bl, i) => {
            let olLy: OlLayerTile | OlLayerImage;
            if (bl.type.toLowerCase() === 'wms') {
                if (bl.singleTile) {
                    olLy = new OlLayerImage({
                        source: new OlSourceImageWMS({
                            url: bl.url, 
                            params: {layers: bl.layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: false}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                            projection: this.olproj
                        }),
                    });
                } else {
                    olLy = new OlLayerTile({
                        source: new OlSourceTileWMS({
                            url: bl.url, 
                            params: {layers: bl.layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: false}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                            projection: this.olproj
                        }),
                    });
                }
                // EL nom s'ha de posar despres ! No està al options
                olLy.set('name', bl.name);
                olLy.set('singleTile', bl.singleTile); // Ens guardem que es singleTile
                olLy.set('visible', (i === 0)); // D'entrada només posem com a visilble el primer
                this.baseLayers.push(olLy);
            } else if (bl.type.toLowerCase() === 'wmts' && bl.cap) {
                olLy = new OlLayerTile({
                    source: new OlSourceWMTS(bl.cap)
                });
                olLy.set('name', bl.name);
                olLy.set('visible', (i === 0)); // D'entrada només posem com a visilble el primer
                this.baseLayers.push(olLy);
            }
        });
        this._props.appState.mapConfig.refLayers.forEach((rl, i) => {
            let olLy: OlLayerTile | OlLayerImage;
            if (rl.type.toLowerCase() === 'wms') {
                if (rl.singleTile) {
                    olLy = new OlLayerImage({
                        source: new OlSourceImageWMS({
                            url: rl.url, 
                            params: {layers: rl.layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: false}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                            projection: this.olproj
                        }),
                    });
                } else {
                    olLy = new OlLayerTile({
                        source: new OlSourceTileWMS({
                            url: rl.url, 
                            params: {layers: rl.layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: false}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                            projection: this.olproj
                        }),
                    });
                }
                // EL nom s'ha de posar despres ! No està al options
                olLy.set('name', rl.name);
                olLy.set('singleTile', rl.singleTile); // Ens guardem que es singleTile
                olLy.set('visible', true); // D'entrada els posem visibles
                this.refLayers.push(olLy);
            } else if (rl.type.toLowerCase() === 'wmts' && rl.cap) {
                olLy = new OlLayerTile({
                    source: new OlSourceWMTS(rl.cap)
                });
                olLy.set('name', rl.name);
                olLy.set('visible', true); // D'entrada només posem com a visilble el primer
                this.refLayers.push(olLy);
            }
        });
        this.olview = new OlView({
            projection: this.olproj, // Projecció ETRS89 UTM 31N
            center: getCenter(this.initialExtent),
            extent: this.initialExtent,
            zoom: 12,
            minZoom: 12,
            maxZoom: 20
        });
        this.olmap = new OlMap({
            layers: this.baseLayers.concat(this.refLayers),
            view: this.olview
        });
        this.olmap.on('postcompose', this.olmap.updateSize);
        if (!this._props.appState.readOnly) {
            this.olmap.on('singleclick', this.setFIDFromEvt);
        }
        const ctrls = this.olmap.getControls();
        // Sembla que el Duration fa que no funcioni
        ctrls.forEach(element => {
            (element as any).duration_ = 0;
        });
        const intrs = this.olmap.getInteractions();
        intrs.forEach(element => {
            (element as any).duration_ = 0;
        });
        // Afegim la capa de fanals
        this.addLayer(this._props.fansLayer, this.mapConfig.wmsELEUrl, null, this._props.fansLayer, true, true, true);

        // Afegim la capa de lots per quan s'ordenin
        this.lotOrdSource = new OlSourceVector();
        this.lotOrdLayer = new OlLayerVector({source: this.lotOrdSource});
        this.lotOrdLayer.set('name', 'lotOrd');
        this.lotOrdLayer.set('singleTile', true);
        this.lotOrdLayer.set('visible', true);
        this.lotOrdLayer.setZIndex(999);
        this.olmap.addLayer(this.lotOrdLayer);
    }

    /** Funció per pintar un polígon
     * @param feature 
     * @param p1 
     */
    private stlp: StyleFunction = (feature: FeatureLike): OlStyleStyle | OlStyleStyle[] => {
        return new OlStyleStyle({
            stroke: new OlStyleStroke({
                color: 'rgba(255, 75, 25, 1.0)',
                width: 3,
                lineDash: [10, 10]
            }),
            fill : new OlStyleFill({color: 'rgba(204, 153, 51, 0.5)'})
        });
    }
    
    /** Funció per pintar un polígon2
     * @param feature 
     * @param p1 
     */
    private stlp2: StyleFunction = (feature: FeatureLike): OlStyleStyle | OlStyleStyle[] => {
        return new OlStyleStyle({
            stroke: new OlStyleStroke({
                color: 'rgba(51, 153, 204, 1.0)',
                width: 3,
                lineDash: [10, 10]
            }),
            fill : new OlStyleFill({color: 'rgba(51, 153, 204, 0.25)'})
        });
    }
    
    // Posa el Lot a la capa de "markers" i centra el mapa
    public setLotOrd(geom: string) {
        const ggeom: Geometry = this.gjson.readGeometry(geom);
        const fea: Feature = new Feature(ggeom);
        fea.setId(1);
        fea.setStyle(this.stlp);
        this.lotOrdSource?.clear();
        this.lotOrdSource.addFeature(fea);
        const ext: Extent = ggeom.getExtent();
        const dx = (ext[2] - ext[0]) * 2.0;
        const dy = (ext[3] - ext[1]) * 2.0;
        this.olview.fit([ext[0] - dx, ext[1] - dy, ext[2] + dx, ext[3] + dy]);
    }

    // Posa el Lot 2 a la capa de "markers"
    public setLotOrd2(geom: string) {
        const ggeom: Geometry = this.gjson.readGeometry(geom);
        const fea: Feature = new Feature(ggeom);
        fea.setId(2);
        fea.setStyle(this.stlp2);
        const fea2: Feature = this.lotOrdSource?.getFeatures().find((fea: Feature) => fea.getId() === 2);
        if (fea2) {
            this.lotOrdSource?.removeFeature(fea2);
        }
        this.lotOrdSource.addFeature(fea);
    }

    // Elimina els lots de la capa de "markers"
    public clearLotOrd() {
        this.lotOrdSource?.clear();
    }

    // Elimina el lot 2 de la capa de "markers"
    public clearLotOrd2() {
        const fea2: Feature = this.lotOrdSource?.getFeatures().find((fea: Feature) => fea.getId() === 2);
        if (fea2) {
            this.lotOrdSource?.removeFeature(fea2);
        }
    }

    // Afegim la capa de lots
    public addLotsLayer() {
        const parms = {
            QFILTER: `ELECOD=${this._props.appState.ele.eleCod} and PARTIT=${this._props.appState.usr.partitCod}`,
            time: new Date().getTime().toString() // Per evitar la cache de servidor
        }
        this.lotsLayer = this.addLayer(this._props.lotsLayer, this.mapConfig.wmsELEUrl, parms, this._props.lotsLayer, true, true, true);
        this.lotsLayer.setOpacity(0.75);
    }

    // Eliminem la capa de lots
    public removeLotsLayer() {
        this.removeLayer(this._props.lotsLayer);
        this.lotsLayer = null;
    }

    // Creem la llista inicial de categories
    private createCats(): Array<ICatLot> {
        let ctsLts: Array<ICatLot> = [];
        this.mapConfig.categories.forEach((ct, i) => {
            ctsLts.push({
                cat: ct.cat,
                nLots: 0,
                nFanals: 0
            });
        });
        return ctsLts;
    }

    // Per posar l'estat de l'HorizontalCollapse
    @action setHCExpanded(expanded: boolean) {
        this.hcExpanded = expanded;
    }

    // Per posar l'estat de l'HorizontalCollapse del RefLayers
    @action setRLHCExpanded(expanded: boolean) {
        this.rlHCExpanded = expanded;
    }

    // Actualitza les mides de la pàgina
    @action updateWindowSize() {
        this.width = window.innerWidth;
        this.height = window.innerHeight;
    }

    // Posa el zoom i la posició a partir de la finca o adreça seleccionada
    @action setZoomPos(response: string) {
        let txt = Util.getBetween('<gml:boundedBy>', '</gml:boundedBy>', response, {pos: 0});
        if (txt != null) {
            txt = Util.getBetween('<gml:coordinates>', '</gml:coordinates>', response, {pos: 0});
            if (txt != null) {
                txt = txt.replace(/ /g, ',');
                const crds = txt.split(',');
                if (crds.length === 4) {
                    const ext: Extent = [parseFloat(crds[0]), parseFloat(crds[1]), parseFloat(crds[2]), parseFloat(crds[3])];
                    this.olview.fit(ext);
                }
            }
        }
    }
    
    // Busquem el FID d'un Numero (a partir del NumCod)
    @action setFIDFromNumCod(numCod: number) {
        if (this._props.adrLayer && numCod) {
            const defaultParameters = {
            SERVICE: 'WFS',
            VERSION: '1.1.0',
            REQUEST: 'GetFeature',
            TYPENAME: this._props.adrLayer,
            OUTPUTFORMAT: 'GML2',
            SRSNAME: 'EPSG:25831',
            FEATUREID: 'adr.' + numCod.toString()
            };
            this.req.getSequentialRequest(this.mapConfig.wfsUrl + Util.getParamString(defaultParameters, this.mapConfig.wfsUrl)).then(action((resultat1: {}) => {
                const newFID = Util.getBetween(this._props.adrLayer + ' fid="', '">', resultat1 as string, {pos: 0});
                if (newFID) {
                    this.numCod = numCod;
                    this.setZoomPos(resultat1 as string);
                }
            }));
        } else {
            this.numCod = 0;
        }
    }

    // Busquem el FID d'un lot (a partir d'una coordenada que ve d'un event)
    @action setFIDFromEvt = (evt: ol.events.Event) => {
        const mevt: MapBrowserEvent = (evt as MapBrowserEvent);
        this.getLOTElement(mevt.coordinate);
    }

    /**
     * Recuperem la el número de lots i fanals de cada categoria
     */
    @action async getCatsLots() {
        const appState = this._props.appState;
        let ctsLts: Array<ICatLot> = this.createCats();
        ctsLts.forEach((ct, i) => {
            ct.nLots = 0;
            ct.nFanals = 0;
        });
        const xreq: string = Util.getXMLRequest('GetEleEstatLotsPartit', ['Session', 'Usuari', 'EleCod'], [appState.usr.sesCod, Util.encodeHTML(appState.usr.userId), appState.ele.eleCod.toString()]);
        const resultat = await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
        const xresp: string = Util.getXMLResponse('GetEleEstatLotsPartit', resultat as string);
        if (xresp) {
            let pos: { pos: number } = { pos: 0 };
            let xcatLot: string = Util.getBetween('catLot>', '</catLot', xresp, pos);
            while (xcatLot) {
                const cat: number = parseInt(Util.getBetween('cat>', '</cat', xcatLot, { pos: 0 }));
                const nLots: number = parseInt(Util.getBetween('lots>', '</lots', xcatLot, { pos: 0 }));
                const nFanals: number = parseInt(Util.getBetween('fanals>', '</fanals', xcatLot, { pos: 0 }));
                let catLot: ICatLot = ctsLts.find((catLot: ICatLot) => { return cat === catLot.cat; });
                if (!catLot) {
                    catLot = ctsLts.find((catLot: ICatLot) => { return cat === catLot.cat; });
                }
                if (catLot) {
                    catLot.nLots += nLots;
                    catLot.nFanals += nFanals;
                }
                xcatLot = Util.getBetween('catLot>', '</catLot', xresp, pos);
            }
        }
        this.catsLots = ctsLts;
    }
    
    /**
     * Posem la categoria d'un lot
     */
    @action async setLotCat(cat: number) {
        if (this.elementData && this.elementData.cat !== cat) {
            const appState = this._props.appState;
            let ctsLts: Array<ICatLot> = [...this.catsLots];
            ctsLts.forEach((ct, i) => {
                ct.nLots = 0;
                ct.nFanals = 0;
            });
            const xreq: string = Util.getXMLRequest('SetEleLotCat', ['Session', 'Usuari', 'EleCod', 'Lot', 'Cat'], [
                appState.usr.sesCod,
                Util.encodeHTML(appState.usr.userId),
                appState.ele.eleCod.toString(),
                this.elementData.lot.toString(),
                cat.toString()
            ]);
            const resultat = await this.req.postRequestRaw(this.mapConfig.wsUrl, xreq, { headers: { 'Content-Type': 'text/xml;' } });
            const xresp: string = Util.getXMLResponse('SetEleLotCat', resultat as string);
            if (xresp) {
                this.getCatsLots();
                //this.setSelect(this.lotsLayer, '');
                //this.lotsLayer.getSource().refresh();
                this.removeLotsLayer();
                this.addLotsLayer();
            }
        } else {
            this.setSelect(this.lotsLayer, '');
        }
        this.elementData = null;
    }
    
    // Llegim un lot a partir d'una coordenada
    public getLOTElement = (c: Coordinate) => {
        const ly: OlLayerBase = this.getLayer(this._props.lotsLayer);
        if (ly) {
            let src: OlSourceImageWMS | OlSourceTileWMS;
            if (ly.get('singleTile')) {
                src = (ly as OlLayerImage).getSource() as OlSourceImageWMS;
            } else {
                src = (ly as OlLayerTile).getSource() as OlSourceTileWMS;
            }
            const url = src.getFeatureInfoUrl(c, this.olview.getResolution(), src.getProjection(), {
                'INFO_FORMAT': 'application/vnd.ogc.gml', 
                'QUERY_LAYERS': this._props.lotsLayer,
                'QFILTER': `ELECOD=${this._props.appState.ele.eleCod} and PARTIT=${this._props.appState.usr.partitCod}`,
                'time': new Date().getTime().toString() // Per evitar la cache de servidor
            });
            // const url = src.getFeatureInfoUrl(c, this.olview.getResolution(), src.getProjection(), {'INFO_FORMAT': 'application/json', 'QUERY_LAYERS': this._props.lotsLayer});
            this.req.getRequest(url).then(action((result: {}) => {
                const fid: string = Util.getBetween( this._props.lotsLayer + ' fid="', '"', result as string, { pos: 0 });
                this.setSelect(this.lotsLayer, fid);
                if (fid) {
                    this.elementData = { 
                        lot: parseInt(Util.getBetween('LOT>', '</LOT', result as string, { pos: 0 })),
                        cat: parseInt(Util.getBetween('CAT>', '</CAT', result as string, { pos: 0 })),
                        fanals: parseInt(Util.getBetween('FAROLES>', '</FAROLES', result as string, { pos: 0 })),
                        carDesc: Util.getBetween('CARDESC>', '</CARDESC', result as string, { pos: 0 })
                    };
                } else {
                    this.elementData = undefined;
                }
            })); 
        }
    }

    public setSelect(layer: OlLayerTile | OlLayerImage, fid: string) {
        delete (layer.getSource() as any).params_.SELECT;
        (layer.getSource() as any).params_.SELECT = fid ? fid : layer.get('name') + '.0';
        layer.getSource().refresh();
    }
    
    // Afegim un layer al mapa
    public addLayer(name: string, url: string, parms: {}, layers: string, singleTile: boolean, transparent: boolean, visible: boolean): OlLayerTile | OlLayerImage { // @observable
        let olLy: OlLayerTile | OlLayerImage;
        if (singleTile) {
            olLy = new OlLayerImage({
                source: new OlSourceImageWMS({
                    url: url, 
                    params: {layers: layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: transparent, ...parms}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                    projection: this.olproj
                }),
            });
        } else {
            olLy = new OlLayerTile({
                source: new OlSourceTileWMS({
                    url: url, 
                    params: {layers: layers, SRS: this.olproj.getCode(), FORMAT: 'image/png', TRANSPARENT: transparent, ...parms}, // Nom de la capa en el servidor WMS, format d'imatge PNG, és una capa base, volem que sigui visible)
                    projection: this.olproj
                }),
            });
        }
        // EL nom s'ha de posar despres ! No està al options
        olLy.set('name', name);
        olLy.set('singleTile', singleTile); // Ens guardem si és singleTile
        olLy.set('visible', visible);
        if (this.olmap) {
            this.olmap.addLayer(olLy);
        }
        return olLy;
    }

    // busquem un layer del mapa
    public getLayer(name: string) : OlLayerBase {
        const lys: OlCollection<OlLayerBase> = this.olmap.getLayers();
        let myLy: OlLayerBase = null;
        lys.forEach((ly: OlLayerBase) => {
            if (ly.get('name') === name) {
                myLy =ly;
            }
        });
        return myLy;
    }

    // Traiem un layer del mapa
    public removeLayer(name: string) {
        const lys: OlCollection<OlLayerBase> = this.olmap.getLayers();
        lys.forEach((ly: OlLayerBase) => {
            if (ly.get('name') === name) {
                this.olmap.removeLayer(ly);
            }
        });
    }

    // // Afegim un layer al mapa
    // public getWFSSource(url: string, layer: string): OlSourceVector { // @observable
    //     const olSrcV: OlSourceVector = new OlSourceVector({
    //         format: new OlFormatGML2({
    //             featureType: layer,
    //             srsName: 'EPSG:25831'
    //         }),
    //         url: url
    //     });
    //     return olSrcV;
    // }


}